import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { adminLogin, adminMe, getAdminToken, setAdminToken } from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const token = getAdminToken();
      if (!token) {
        if (alive) setBooting(false);
        return;
      }
      try {
        const me = await adminMe();
        if (alive) setUser(me);
      } catch {
        setAdminToken(null);
        if (alive) setUser(null);
      } finally {
        if (alive) setBooting(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await adminLogin(username, password);
    setAdminToken(res.access_token);
    const me = await adminMe();
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(() => {
    setAdminToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, booting, isAuthenticated: !!user, login, logout }),
    [user, booting, login, logout],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
